import * as core from 'cw-ui-core';
import Routes from 'src/services/Routes';
import Locale from 'src/Locale';

export const ROLE_FILTER_KIND = {
    IDENTITY: 'identity',
};

export const buildRoleFilterKey = (kind, roleId) => `${kind}:${roleId}`;

export const getIdentityRoleId = role => role?.id || role?.Id || null;

export const getIdentityRoleName = role => role?.name || role?.Name || '';

export const parseIdentityRoleIdsFromKeys = selectedKeys =>
    (selectedKeys || [])
        .map(key => {
            const separatorIndex = key.indexOf(':');
            if (separatorIndex === -1) {
                return null;
            }
            const kind = key.slice(0, separatorIndex);
            const roleId = key.slice(separatorIndex + 1);
            if (!kind || !roleId || kind !== ROLE_FILTER_KIND.IDENTITY) {
                return null;
            }
            return roleId;
        })
        .filter(Boolean);

export const toSelectedFilterFromKey = (key, catalogs) => {
    const separatorIndex = key.indexOf(':');
    if (separatorIndex === -1) {
        return null;
    }
    const kind = key.slice(0, separatorIndex);
    const roleId = key.slice(separatorIndex + 1);
    if (!kind || !roleId || kind !== ROLE_FILTER_KIND.IDENTITY) {
        return null;
    }

    const role = catalogs.identityRoles?.find(item => getIdentityRoleId(item) === roleId);
    return role
        ? { key, kind, roleId: getIdentityRoleId(role), label: getIdentityRoleName(role) }
        : { key, kind, roleId, label: roleId };
};

export const getRoleFilterActive = state =>
    !!core.getScreenCustom(state.screen, ['roleFilterActive']) ||
    !!core.getModuleCache(state.module, [Routes.USERS.id, 'roleFilterActive']);

export const getRoleFilterResults = state => {
    const cachedResults = core.getModuleCache(state.module, [Routes.USERS.id, 'roleFilterResults']);
    if (cachedResults) {
        return cachedResults;
    }
    return core.getScreenCustom(state.screen, ['roleFilterResults']);
};

export const isRoleFilterViewActive = state => getRoleFilterActive(state) || !!getRoleFilterResults(state);

const getUserSearchableFields = user => ({
    id: user?.id || user?.Id || user?.userId || user?.UserId || '',
    email: user?.email || user?.Email || '',
    firstName: user?.firstName || user?.FirstName || '',
    lastName: user?.lastName || user?.LastName || '',
});

const isValidEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const isUserSearchTermTooShort = searchTerm => {
    const search = (searchTerm || '').trim();
    return search.length > 0 && search.length < 3;
};

export const filterUsersBySearch = (users, searchTerm) => {
    const search = (searchTerm || '').trim();
    if (!search || isUserSearchTermTooShort(search)) {
        return users || [];
    }

    const lowerSearch = search.toLowerCase();

    if (search.startsWith('@')) {
        return (users || []).filter(user => {
            const email = (user?.email || user?.Email || '').toLowerCase();
            return email.endsWith(lowerSearch) || email.includes(lowerSearch);
        });
    }

    if (isValidEmail(search)) {
        return (users || []).filter(user => {
            const email = (user?.email || user?.Email || '').toLowerCase();
            return email === lowerSearch;
        });
    }

    if (/^[0-9][a-zA-Z0-9]+$/.test(search)) {
        return (users || []).filter(user => {
            const id = (user?.id || user?.Id || user?.userId || user?.UserId || '').toLowerCase();
            return id === lowerSearch || id.startsWith(lowerSearch);
        });
    }

    return (users || []).filter(user => {
        const { id, email, firstName, lastName } = getUserSearchableFields(user);
        const values = [email, firstName, lastName, id].map(value => (value || '').toLowerCase());
        return values.some(value => value.startsWith(lowerSearch));
    });
};

export const validateUserSearchTerm = searchTerm =>
    isUserSearchTermTooShort(searchTerm) ? Locale.user_search_min_length.defaultMessage : null;
